import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Download, CheckCircle2, Stethoscope, Calendar, ArrowRight, Phone, ShieldCheck, FileText, Clock } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
export function NewPatients() {
  useScreenInit();
  const insurers = ['Medicaid / Maryland Healthy Smiles', 'Delta Dental', 'Aetna', 'Cigna', 'MetLife', 'Guardian', 'Humana', 'UnitedHealthcare'];
  return <div className="min-h-screen bg-white selection:bg-teal-100 selection:text-teal-900" data-id="element-242">
      <Header data-id="element-243" />

      <main className="pt-40" data-id="element-244">
        {/* Welcoming Onboarding Hero */}
        <section className="pb-24 px-4" data-id="element-245">
          <div className="max-w-7xl mx-auto" data-id="element-246">
            <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-247">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-248">
                <h1 className="text-4xl md:text-6xl font-bold text-[#0F2C59] mb-6 leading-tight" data-id="element-249">
                  WELCOMING NEW PATIENTS TO COLESVILLE DENTAL CENTER.
                </h1>
                <p className="text-xl text-[#4F709C] leading-relaxed max-w-xl" data-id="element-250">
                  We make family dental care simple, highly transparent, and
                  entirely stress-free from day one.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} className="grid sm:grid-cols-2 gap-4" data-id="element-251">
                <div className="bg-white border-2 border-slate-100 p-6 rounded-[12px] shadow-sm hover:shadow-md transition-shadow group" data-id="element-252">
                  <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center mb-4 text-[#0D9488] group-hover:bg-[#0D9488] group-hover:text-white transition-colors" data-id="element-253">
                    <Download size={20} data-id="element-254" />
                  </div>
                  <h3 className="font-bold text-[#0F2C59] mb-4 text-sm uppercase tracking-wider" data-id="element-255">
                    Medicaid New Patient Packet
                  </h3>
                  <button className="text-[#0D9488] font-bold text-xs flex items-center gap-1 hover:underline" data-id="element-256">
                    Download PDF <ArrowRight size={14} data-id="element-257" />
                  </button>
                </div>
                <div className="bg-white border-2 border-slate-100 p-6 rounded-[12px] shadow-sm hover:shadow-md transition-shadow group" data-id="element-258">
                  <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center mb-4 text-[#0D9488] group-hover:bg-[#0D9488] group-hover:text-white transition-colors" data-id="element-259">
                    <Download size={20} data-id="element-260" />
                  </div>
                  <h3 className="font-bold text-[#0F2C59] mb-4 text-sm uppercase tracking-wider" data-id="element-261">
                    Private Insurance New Patient Forms
                  </h3>
                  <button className="text-[#0D9488] font-bold text-xs flex items-center gap-1 hover:underline" data-id="element-262">
                    Download PDF <ArrowRight size={14} data-id="element-263" />
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Chronological Care Journey */}
        <section className="py-24 bg-slate-50" data-id="element-264">
          <div className="max-w-7xl mx-auto px-4" data-id="element-265">
            <div className="text-center mb-20" data-id="element-266">
              <h2 className="text-3xl font-bold text-[#0F2C59] mb-4" data-id="element-267">
                YOUR JOURNEY TO A BETTER SMILE
              </h2>
              <div className="w-24 h-1 bg-[#0D9488] mx-auto rounded-full" data-id="element-268" />
            </div>

            <div className="space-y-12" data-id="element-269">
              {/* Phase 1: Before */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white rounded-[12px] shadow-sm border border-slate-100 overflow-hidden" data-id="element-270">
                <div className="grid md:grid-cols-4" data-id="element-271">
                  <div className="bg-[#0F2C59] p-8 text-white flex flex-col justify-center items-center text-center" data-id="element-272">
                    <div className="text-teal-400 mb-4" data-id="element-273">
                      <Clock size={40} data-id="element-274" />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] mb-2 uppercase opacity-60" data-id="element-275">
                      Phase 1
                    </span>
                    <h3 className="text-xl font-bold" data-id="element-276">
                      BEFORE YOUR APPOINTMENT
                    </h3>
                  </div>
                  <div className="md:col-span-3 p-8 md:p-12" data-id="element-277">
                    <h4 className="font-bold text-[#0F2C59] mb-6 text-lg" data-id="element-278">
                      Initial Preparation Checklist:
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-y-4 gap-x-8 mb-8" data-id="element-279">
                      {['Previous dental X-rays', 'Current medication list', 'State or private insurance cards', 'Medical histories (BP, Diabetes, etc.)'].map((item, i) => <div key={i} className="flex items-center gap-3 text-slate-600" data-id="element-280">
                          <CheckCircle2 size={18} className="text-[#0D9488] flex-shrink-0" data-id="element-281" />
                          <span className="text-sm font-medium" data-id="element-282">{item}</span>
                        </div>)}
                    </div>
                    <a href="#" className="inline-flex items-center gap-2 text-[#0D9488] font-bold text-sm hover:gap-3 transition-all" data-id="element-283">
                      Pre-Fill Digital Intake Forms <ArrowRight size={16} data-id="element-284" />
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Phase 2: During */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white rounded-[12px] shadow-sm border border-slate-100 overflow-hidden" data-id="element-285">
                <div className="grid md:grid-cols-2" data-id="element-286">
                  <div className="h-64 md:h-auto overflow-hidden" data-id="element-287">
                    <img src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&q=80&w=800" alt="Clinical Exam" className="w-full h-full object-cover" data-id="element-288" />
                  </div>
                  <div className="p-8 md:p-12" data-id="element-289">
                    <div className="flex items-center gap-2 mb-4" data-id="element-290">
                      <span className="text-xs font-bold tracking-[0.2em] text-[#0D9488] uppercase" data-id="element-291">
                        Phase 2
                      </span>
                      <div className="h-px flex-grow bg-slate-100" data-id="element-292" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0F2C59] mb-6" data-id="element-293">
                      DURING YOUR APPOINTMENT
                    </h3>
                    <div className="space-y-4 text-slate-600 text-sm leading-relaxed mb-8" data-id="element-294">
                      <p data-id="element-295">
                        Your visit begins with a comprehensive oral health
                        mapping and cancer screening. We use high-resolution
                        digital diagnostics to build your personalized smile
                        plan.
                      </p>
                      <p data-id="element-296">
                        Our team will walk you through every finding, ensuring
                        you have a complete understanding of your clinical
                        status before any treatment begins.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-297">
                      <span className="bg-teal-50 text-[#0D9488] text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider border border-teal-100" data-id="element-298">
                        Medicaid Verification Handled Instantly
                      </span>
                      <span className="bg-slate-50 text-[#4F709C] text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider border border-slate-100" data-id="element-299">
                        Flexible 3-Year Financing
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Phase 3: After */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white rounded-[12px] shadow-sm border border-slate-100 overflow-hidden" data-id="element-300">
                <div className="grid md:grid-cols-4" data-id="element-301">
                  <div className="bg-[#4F709C] p-8 text-white flex flex-col justify-center items-center text-center" data-id="element-302">
                    <div className="text-white mb-4" data-id="element-303">
                      <ShieldCheck size={40} data-id="element-304" />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] mb-2 uppercase opacity-60" data-id="element-305">
                      Phase 3
                    </span>
                    <h3 className="text-xl font-bold" data-id="element-306">AFTER YOUR VISIT</h3>
                  </div>
                  <div className="md:col-span-3 p-8 md:p-12" data-id="element-307">
                    <h4 className="font-bold text-[#0F2C59] mb-4 text-lg" data-id="element-308">
                      Long-Term Continuity & Care:
                    </h4>
                    <p className="text-slate-600 text-sm leading-relaxed mb-8 max-w-2xl" data-id="element-309">
                      Experience a transparent checkout flow where we provide
                      printed custom at-home care instructions. We'll help you
                      book your future 6-month routine appointments on-site or
                      through our secure online portal to ensure your smile
                      stays healthy.
                    </p>
                    <button className="bg-[#0F2C59] text-white px-6 py-3 rounded-lg font-bold text-sm hover:bg-[#1a3d73] transition-colors" data-id="element-310">
                      Access Patient Portal
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Insurance Trust Grid */}
        <section className="py-24 bg-white" data-id="element-311">
          <div className="max-w-7xl mx-auto px-4" data-id="element-312">
            <h2 className="text-center text-xl font-bold text-[#0F2C59] mb-16 tracking-[0.1em] uppercase" data-id="element-313">
              INSURANCES ACCEPTED & VERIFIED HERE
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16" data-id="element-314">
              {insurers.map((name, i) => <div key={i} className="h-20 border border-slate-100 rounded-lg flex items-center justify-center p-4 grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all cursor-default" data-id="element-315">
                  <span className="text-[10px] font-bold text-center text-slate-400 uppercase tracking-tighter" data-id="element-316">
                    {name}
                  </span>
                </div>)}
            </div>
            <div className="text-center" data-id="element-317">
              <a href="#" className="text-[#0D9488] font-bold text-sm flex items-center justify-center gap-2 hover:underline" data-id="element-318">
                Don't See Your Plan? Check Your Custom Coverage Instantly{' '}
                <ArrowRight size={16} data-id="element-319" />
              </a>
            </div>
          </div>
        </section>

        {/* Final Conversion Banner */}
        <section className="py-20 px-4" data-id="element-320">
          <div className="max-w-5xl mx-auto bg-[#0F2C59] rounded-[12px] p-10 md:p-16 text-center text-white shadow-2xl relative overflow-hidden" data-id="element-321">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32" data-id="element-322" />
            <div className="relative z-10" data-id="element-323">
              <h2 className="text-3xl md:text-4xl font-bold mb-10" data-id="element-324">
                Ready to Experience Painless,
                <br data-id="element-325" />
                Compassionate Dental Care?
              </h2>
              <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-326">
                <button className="bg-[#0D9488] hover:bg-[#0F766E] text-white px-10 py-4 rounded-lg font-bold transition-all shadow-lg shadow-teal-900/20" data-id="element-327">
                  Book Online 24/7
                </button>
                <button className="border-2 border-white/30 hover:border-white text-white px-10 py-4 rounded-lg font-bold transition-all" data-id="element-328">
                  Call Office: (410) 777-6984
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-329" />
    </div>;
}